/**
 * HubQuote — daily inspiration (Phase X.5.1).
 *
 * One quote per calendar day, deterministic by day-of-epoch index so every
 * user sees the same quote on a given day (no server, no storage). Manual
 * browse controls step through the bank; the center dot returns to today's
 * quote. Honors prefers-reduced-motion: the fade is suppressed when the
 * user has asked for reduced motion (and in jsdom, where matchMedia is
 * absent, motion defaults off).
 *
 * Attribution uses an en dash (U+2013), not an em dash — em dashes are
 * banned from brand-facing copy per brand rule #1.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { HUB_QUOTES } from '@/lib/hubQuotes';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Stable index into the quote bank for today (UTC day-of-epoch). */
function todayQuoteIndex(count: number): number {
  if (count <= 0) return 0;
  const dayIndex = Math.floor(Date.now() / 86_400_000);
  return ((dayIndex % count) + count) % count;
}

function CtrlButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex h-7 min-w-7 items-center justify-center rounded-full border border-sage-300/45 bg-white px-2 text-sage-500 transition-colors hover:bg-sage-50 hover:text-sage-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
    >
      {children}
    </button>
  );
}

export interface HubQuoteProps {
  /**
   * 'hero' (X.6.3) renders just today's quote in italic Playfair on the forest
   * hero band — no browse controls, no attribution, no card. 'standalone'
   * (default) is the original card with browse controls + attribution.
   */
  variant?: 'standalone' | 'hero';
}

export function HubQuote({ variant = 'standalone' }: HubQuoteProps = {}): ReactNode {
  const count = HUB_QUOTES.length;
  const reduceMotion = useMemo(prefersReducedMotion, []);
  const [index, setIndex] = useState(() => todayQuoteIndex(count));

  if (count === 0) return null;

  // Hero variant: quote-only, cream-on-forest, no controls (matches the Warm
  // prototype hero). Always today's quote — browsing lives in the standalone.
  //
  // Founder walk 2026-07-06: the quote runs at the hero band's FULL content
  // column (no private max-width cap — the old 540px cap wrapped a
  // one-line quote into two). A genuinely long quote wraps at the band
  // frame's width; the font never shrinks to force one line.
  if (variant === 'hero') {
    const todayQuote = HUB_QUOTES[todayQuoteIndex(count)];
    return (
      <div data-testid="hub-quote">
        <p className="font-display text-[16px] italic leading-relaxed text-cream/80 text-balance md:text-[19px]">
          {`“${todayQuote.q}”`}
        </p>
        <p
          data-testid="hub-quote-by"
          className="mt-2.5 flex items-center gap-2.5 text-[12px] font-semibold tracking-wide text-warm-400"
        >
          <span aria-hidden="true" className="h-px w-[22px] bg-warm-400" />
          {todayQuote.by}
        </p>
      </div>
    );
  }

  const quote = HUB_QUOTES[index];
  const step = (delta: number): void =>
    setIndex((i) => ((i + delta) % count + count) % count);
  const goToday = (): void => setIndex(todayQuoteIndex(count));

  return (
    <section className="mx-auto mb-7 max-w-[760px] px-6 py-3.5 text-center" aria-label="Daily quote">
      <div
        key={index}
        className={reduceMotion ? undefined : 'animate-fade-in'}
        data-testid="hub-quote"
      >
        <p className="font-display text-[18px] italic leading-relaxed text-sage-700 text-balance">
          {`“${quote.q}”`}
        </p>
        <p className="mt-2 text-[12.5px] font-semibold tracking-wide text-warm-500" data-testid="hub-quote-by">
          {`– ${quote.by}`}
        </p>
      </div>
      <div className="mt-3 flex justify-center gap-2">
        <CtrlButton onClick={() => step(-1)} label="Previous quote">
          {'‹'}
        </CtrlButton>
        <CtrlButton onClick={goToday} label="Today's quote">
          {'•'}
        </CtrlButton>
        <CtrlButton onClick={() => step(1)} label="Next quote">
          {'›'}
        </CtrlButton>
      </div>
    </section>
  );
}
